import type { OfferType, PaymentTerm, Terms } from '@makemydeal/dr-platform-types';
import { FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import type { TermIdParam } from '../types/paymentTypes';
import type { MergedTerms } from '../types/termsTypes';
import * as selectors from '../selectors';
import { isExtensionValueValid, parseTermId } from './termIdParser';
import { OFFER_TERMS } from '../constants/state';
import { TermTypeWithExtension } from '../constants/payment';

/**
 * Returns default terms structure with optional overrides
 * @param overrides - Optional terms to override defaults
 * @returns Terms object with default structure
 */
export const getDefaultTerms = (overrides: Terms = OFFER_TERMS): Terms => {
    return {
        byId: { ...overrides.byId },
        allIds: [...overrides.allIds],
        mappings: { ...overrides.mappings },
        leaseTerms: [...overrides.leaseTerms!],
        financeTerms: [...overrides.financeTerms!],
        balloonTerms: [...overrides.balloonTerms!]
    };
};

/**
 * Merges two Terms objects, combining IDs and mappings
 * @param prevTerms - Previous terms state
 * @param newTerms - New terms to merge
 * @returns Merged Terms object
 */
export const mergeTerms = (prevTerms: Terms, newTerms: Terms): MergedTerms => {
    const mergedTermsNew = {
        byId: {
            ...prevTerms.byId,
            ...newTerms.byId
        },
        allIds: Array.from(new Set([...prevTerms.allIds, ...newTerms.allIds])),
        financeTerms: Array.from(new Set([...prevTerms.financeTerms!, ...newTerms.financeTerms!])),
        leaseTerms: Array.from(new Set([...prevTerms.leaseTerms!, ...newTerms.leaseTerms!])),
        balloonTerms: Array.from(new Set([...prevTerms.balloonTerms!, ...newTerms.balloonTerms!])),
        mappings: { ...prevTerms.mappings, ...newTerms.mappings }
    };

    return mergedTermsNew;
};

/**
 * Checks if a term ID has incomplete parameters
 * @param termIdParams - Term ID parameters to validate
 * @param fordFlexBuy - Optional Ford FlexBuy value
 * @returns True if term ID is incomplete
 */
export const isTermIdIncomplete = (termIdParams: TermIdParam, fordFlexBuy?: number): boolean => {
    const types = fordFlexBuy
        ? Object.values(TermTypeWithExtension)
        : Object.values(TermTypeWithExtension).filter((item) => item !== TermTypeWithExtension.FORDFLEXBUY);
    return Boolean(types.find((key) => !isExtensionValueValid(termIdParams[key])));
};

/**
 * Gets a clean term ID from a potentially incomplete term ID
 * @param termId - Original term ID
 * @param term - Payment term object
 * @returns Object with offerType and cleaned termId, or undefined if invalid
 */
export const getCleanTermId = (termId: string, term: PaymentTerm): { offerType: OfferType; termId: string } | undefined => {
    const idParams = parseTermId(termId);

    if (!idParams || Object.keys(idParams).length === 0) {
        return undefined;
    }

    const { offerType, termIdParams } = idParams;

    const termMonths = term.months || termIdParams.term;
    const downPayment = term.requestedDownPayment;
    const fordFlexBuy = term.initialPaymentDiscount;
    const isIncomplete = isTermIdIncomplete(termIdParams, fordFlexBuy);

    const id = isIncomplete
        ? selectors.getTermId(offerType, termMonths, {
              ...(downPayment && { DOWNPAYMENT: downPayment }),
              ...(fordFlexBuy && { FORDFLEXBUY: fordFlexBuy })
          }) || termId
        : termId;

    return { offerType, termId: id };
};

/**
 * Transforms old terms structure to use new standardized term IDs
 * @param oldTerms - Terms with old ID structure
 * @returns Terms with cleaned/standardized IDs
 */
export const transformTermsToUseNewId = (oldTerms: Terms = getDefaultTerms()): Terms => {
    const newTerms: Terms = {
        byId: {},
        allIds: [],
        financeTerms: [],
        leaseTerms: [],
        balloonTerms: [],
        mappings: {}
    };

    Object.values(oldTerms.byId || {}).forEach((term) => {
        const cleanTermId = getCleanTermId(term.id, term);

        if (cleanTermId == null) {
            return;
        }

        const { termId, offerType } = cleanTermId;

        newTerms.byId![termId] = {
            ...term,
            id: termId
        };

        if (offerType === FINANCE) {
            newTerms.financeTerms?.push(termId);
        } else if (offerType === LEASE) {
            newTerms.leaseTerms?.push(termId);
        } else {
            newTerms.balloonTerms?.push(termId);
        }

        newTerms.allIds.push(termId);
    });

    return newTerms;
};
